import { useState, useEffect, useRef } from "react";
import { Button } from "@/shared/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronLeft, Loader2 } from "lucide-react";
import { MeetingHistoryService } from "@/features/Assistant/services/meetingHistoryService";
import { MeetingCard } from "@/features/Assistant/components/history/MeetingCard";
import type { SSESubscription } from "@/shared/utils/fetchSSE";
import type { Meeting, MeetingEvent } from "@/features/Assistant/types/meetingHistory";

export const AssistantHistory = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState("7");
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<SSESubscription | null>(null);

  useEffect(() => {
    const periodDays = parseInt(period);

    const handleUpdate = (events: MeetingEvent[]) => {
      const groupedMeetings = MeetingHistoryService.groupBySession(events);
      setMeetings(groupedMeetings);
      setLoading(false);
      setError(null);
    };

    const handleError = () => {
      setError(t('assistant.history.connectionError'));
      setLoading(false);
    };

    eventSourceRef.current = MeetingHistoryService.createHistoryStream(
      periodDays,
      handleUpdate,
      handleError
    );

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [period, t]);

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    setCurrentPage(1);
    setExpandedSession(null);
    setLoading(true);
  };

  const toggleSession = (sessionId: string) => {
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };

  const totalPages = Math.ceil(meetings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMeetings = meetings.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setExpandedSession(null);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
    setExpandedSession(null);
  };

  return (
    <div className="pt-6 pb-6 w-full space-y-4">
      {/* Period Filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">{t("assistant.history.title")}</span>
        <div className="flex gap-1 ml-auto">
          <Button variant={period === "1" ? "default" : "ghost"} size="sm" onClick={() => handlePeriodChange("1")} className="text-xs h-7">
            {t("assistant.history.today")}
          </Button>
          <Button variant={period === "7" ? "default" : "ghost"} size="sm" onClick={() => handlePeriodChange("7")} className="text-xs h-7">
            {t("assistant.history.last7Days")}
          </Button>
          <Button variant={period === "30" ? "default" : "ghost"} size="sm" onClick={() => handlePeriodChange("30")} className="text-xs h-7">
            {t("assistant.history.last30Days")}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="py-24 text-center border rounded-lg bg-muted/20">
          <div className="flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t("assistant.history.loading")}</p>
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="py-6 px-4 text-center border rounded-lg bg-red-500/10 border-red-500/20">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}

      {!loading && !error && meetings.length === 0 && (
        <div className="py-24 text-center border rounded-lg bg-muted/20">
          <p className="text-sm text-muted-foreground">{t("assistant.history.noMeetingEvents")}</p>
        </div>
      )}

      {!loading && !error && meetings.length > 0 && (
        <>
          <div className="space-y-2">
            {paginatedMeetings.map((meeting) => (
              <MeetingCard
                key={meeting.sessionId}
                meeting={meeting}
                isExpanded={expandedSession === meeting.sessionId}
                onToggle={toggleSession}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{t("assistant.schedule.rowsPerPage")}</span>
              <Select value={String(itemsPerPage)} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className="w-16 h-7">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="h-7 px-2">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground px-2">
                {t("assistant.schedule.pagination", { current: currentPage, total: totalPages })}
              </span>
              <Button variant="ghost" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages} className="h-7 px-2">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
